import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import './styles/brand-tokens.css';
import './styles/main.css';
import './styles/style.css';
import './styles/floating-icons-fix.css';
import './styles/auth.css';
import AuthModal from './components/AuthModal';
import AuthCallback from './components/AuthCallback';
import MainContent from './components/MainContent';
import FloatingIcons from './components/FloatingIcons';
import Header from './components/Header';
import Hero from './components/Hero';
import GraphsSection from './components/GraphsSection';
import ChatComparison from './components/ChatComparison';
import Notification from './components/Notification';
import { useOptimizationStore } from './store/optimizationStore';
import { AuthProvider, useAuthContext } from './contexts/AuthContext';
import { auth } from './services/api';
import { PKCE } from './utils/pkce';
import ProfilePage from './components/ProfilePage';
import Onboarding from './components/Onboarding';
import MultiAgentPage from './components/MultiAgentPage';
import AdminPage from './components/AdminPage';
import ConnectorPage from './components/ConnectorPage';
import DatasetsPage from './components/DatasetsPage';
import FineTuningGraphPage from './components/FineTuningGraphPage';

const HomeLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', minHeight: '100vh', background: '#FFFFFF' }}>
    <div className="main-content" style={{ flex: 1 }}>
      <FloatingIcons />
      {children}
    </div>
  </div>
);

const SidebarLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="sidebar-layout" style={{ display: 'flex', minHeight: '100vh', background: '#FFFFFF' }}>
    <div className="sidebar-main-content" style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
      <FloatingIcons />
      {children}
    </div>
  </div>
);

const ProfileLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', minHeight: '100vh', background: '#FFFFFF' }}>
    <div className="main-content" style={{ flex: 1 }}>
      <FloatingIcons />
      {children}
    </div>
  </div>
);

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthContext();
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
};

/** Guests: Multi-Agent home. Authenticated users: land on Connector first. */
const DefaultLanding: React.FC<{
  activeModal: 'login' | 'register' | null;
  setActiveModal: React.Dispatch<React.SetStateAction<'login' | 'register' | null>>;
}> = ({ activeModal, setActiveModal }) => {
  const { isAuthenticated, isLoading } = useAuthContext();
  if (isLoading) {
    return (
      <div
        style={{
          padding: '40px',
          textAlign: 'center',
          minHeight: '50vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className="spinner spinner--small"></div>
        <p style={{ marginTop: 12 }}>Загрузка…</p>
      </div>
    );
  }
  if (isAuthenticated) {
    return <Navigate to="/connector" replace />;
  }
  return (
    <SidebarLayout>
      <Header onLoginClick={() => setActiveModal('login')} onRegisterClick={() => setActiveModal('register')} />
      <MultiAgentPage onShowLoginModal={() => setActiveModal('login')} />
      {activeModal && (
        <AuthModal type={activeModal} isOpen={true} onClose={() => setActiveModal(null)} />
      )}
    </SidebarLayout>
  );
};

const RequireAdmin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuthContext();
  if (isLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div className="spinner spinner--small"></div>
        <p>Загрузка…</p>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/" replace />;
  const role = (user as any)?.role;
  const isAdmin = typeof role === 'string' && role.toLowerCase() === 'admin';
  if (!isAdmin) {
    // Дополнительная защита - редирект на главную
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const HomePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState<'login' | 'register' | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  // Используем глобальное состояние для оптимизации
  const { 
    isOptimizing, 
    optimizationProgress, 
    setIsOptimizing, 
    setOptimizationProgress,
    graphSliderValue,
    setGraphSliderValue,
    selectedIterationIndex,
    setSelectedIterationIndex,
    optimizedPrompts,
    optimizationType,
    setOptimizationType
  } = useOptimizationStore();

  // Обработка параметра optimizationType из URL
  useEffect(() => {
    const urlOptimizationType = searchParams.get('optimizationType');
    if (urlOptimizationType && (urlOptimizationType === 'standard' || urlOptimizationType === 'with_integration')) {
      setOptimizationType(urlOptimizationType);
      // Очищаем URL параметр после установки, но остаемся на /home
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('optimizationType');
      navigate(`/home?${newSearchParams.toString()}`, { replace: true });
    }
  }, [searchParams, setOptimizationType, navigate]);

  // Динамическое изменение цвета слайдера
  useEffect(() => {
    const slider = document.querySelector('.slider-bullets') as HTMLInputElement;
    if (slider) {
      const value = selectedIterationIndex;
      const max = optimizedPrompts.length > 0 ? optimizedPrompts.length - 1 : 0;
      const percentage = max > 0 ? (value / max) * 100 : 0;
      const gradient = `#FF8300`;
      slider.style.background = gradient;
    }
  }, [selectedIterationIndex, optimizedPrompts.length]);

  // Синхронизируем graphSliderValue с selectedIterationIndex для графиков
  useEffect(() => {
    if (graphSliderValue !== selectedIterationIndex) {
      setGraphSliderValue(selectedIterationIndex);
    }
  }, [selectedIterationIndex, graphSliderValue, setGraphSliderValue]);

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value);
    setSelectedIterationIndex(newValue);
    setGraphSliderValue(newValue);
  };

  return (
    <HomeLayout>
      <Header
        onLoginClick={() => setActiveModal('login')}
        onRegisterClick={() => setActiveModal('register')}
        onHelpClick={() => setShowOnboarding(true)}
      />
      <div className="container">
        <Hero onLearnMoreClick={() => setShowOnboarding(true)} />
        <MainContent 
          isOptimizing={isOptimizing}
          setIsOptimizing={setIsOptimizing}
          optimizationProgress={optimizationProgress}
          setOptimizationProgress={setOptimizationProgress}
          onShowLoginModal={() => setActiveModal('login')}
        />
        {/* Слайдер вынесен в отдельный блок */}
        <div className="slider-block" id="slider-section">
          <h3 className="slider-title">Выберите новую модель</h3>
          <div className="slider-container">
            <input
              type="range"
              className="slider-bullets"
              min="0"
              max={optimizedPrompts.length > 0 ? optimizedPrompts.length - 1 : 0}
              value={selectedIterationIndex}
              onChange={handleSliderChange}
              step="1"
              disabled={optimizedPrompts.length === 0}
            />
          </div>
          <div className="slider-tooltip">
            {optimizedPrompts.length > 0 ? `Iteration ${selectedIterationIndex + 1}` : ''}
          </div>
          <div className="slider-points-labels">
            {optimizedPrompts.length > 0 ? (
              optimizedPrompts.map((_, i) => (
                <div
                  key={i}
                  className={`slider-point-label ${i <= selectedIterationIndex ? 'active' : ''}`}
                >
                  {i + 1}
                </div>
              ))
            ) : null}
          </div>
        </div>
        <div className="evaluation-block" id="evaluation-section">
          <h2 className="section-title">Оценка</h2>
          <GraphsSection 
            isOptimizing={isOptimizing}
            progress={optimizationProgress}
          />
        </div>
        <div className="chat-block" id="chat-comparison">
          <h2 className="section-title">Чат</h2>
          <ChatComparison onShowLoginModal={() => setActiveModal('login')} />
        </div>
      </div>
      
      {activeModal && (
        <AuthModal
          type={activeModal}
          isOpen={true}
          onClose={() => setActiveModal(null)}
        />
      )}
      
      <Onboarding 
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
      />
    </HomeLayout>
  );
};

const AuthWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuthContext();
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  const processedTokens = useRef<Set<string>>(new Set());

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      navigate(`/auth/auth-link/callback?token=${token}`, { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    const token = searchParams.get('token');
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    
    if (error && !isProcessingAuth) {
      navigate('/', { replace: true });
      return;
    }
    
    if (code && state && !isProcessingAuth && !processedTokens.current.has(code) && !isAuthenticated) {
      navigate('/', { replace: true });
      
      setIsProcessingAuth(true);
      
      const handleGoogleCallback = async () => {
        try {
          const pkceData = PKCE.getPKCEData();
          
          if (!pkceData.codeVerifier || pkceData.state !== state) {
            throw new Error('Invalid PKCE data or state mismatch');
          }
          
          const response = await auth.googleCallback(code, pkceData.codeVerifier);
          
          if (response.success && response.data) {
            login(
              response.data.email,
              response.data.name,
              response.data.company_name
            );
            
            processedTokens.current.add(code);
            PKCE.clearPKCEData();
          } else {
            processedTokens.current.add(code);
            PKCE.clearPKCEData();
          }
        } catch (err: any) {
          console.error('Google OAuth callback error:', err);
          processedTokens.current.add(code);
          PKCE.clearPKCEData();
        } finally {
          setIsProcessingAuth(false);
        }
      };

      handleGoogleCallback();
      return;
    }
  }, [searchParams, login, navigate, isProcessingAuth, isAuthenticated]);

  return (
    <>
      {children}
      {isProcessingAuth && (
        <div className="auth-processing-overlay">
          <div className="auth-processing-content">
            <div className="loading-spinner"></div>
            <h3>Авторизация…</h3>
            <p>Обрабатываем запрос</p>
          </div>
        </div>
      )}
    </>
  );
};

const App: React.FC = () => {
  const [activeModal, setActiveModal] = useState<'login' | 'register' | null>(null);

  return (
    <AuthProvider>
      <AuthWrapper>
        <Routes>
          <Route path="/" element={<DefaultLanding activeModal={activeModal} setActiveModal={setActiveModal} />} />
          <Route path="/multi-agent" element={
            <SidebarLayout>
              <Header onLoginClick={() => setActiveModal('login')} onRegisterClick={() => setActiveModal('register')} />
              <MultiAgentPage onShowLoginModal={() => setActiveModal('login')} />
              {activeModal && (
                <AuthModal
                  type={activeModal}
                  isOpen={true}
                  onClose={() => setActiveModal(null)}
                />
              )}
            </SidebarLayout>
          } />
          <Route path="/auth/auth-link" element={<AuthCallback />} />
          <Route path="/auth/auth-link/callback" element={<AuthCallback />} />
          <Route path="/profile" element={
            <RequireAuth>
              <ProfileLayout>
                <Header onLoginClick={() => setActiveModal('login')} onRegisterClick={() => setActiveModal('register')} />
                <ProfilePage />
                {activeModal && (
                  <AuthModal
                    type={activeModal}
                    isOpen={true}
                    onClose={() => setActiveModal(null)}
                  />
                )}
              </ProfileLayout>
            </RequireAuth>
          } />
          <Route path="/connector" element={
            <RequireAuth>
              <SidebarLayout>
                <Header onLoginClick={() => setActiveModal('login')} onRegisterClick={() => setActiveModal('register')} />
                <ConnectorPage />
                {activeModal && (
                  <AuthModal
                    type={activeModal}
                    isOpen={true}
                    onClose={() => setActiveModal(null)}
                  />
                )}
              </SidebarLayout>
            </RequireAuth>
          } />
          <Route path="/admin" element={
            <RequireAdmin>
              <ProfileLayout>
                <Header onLoginClick={() => setActiveModal('login')} onRegisterClick={() => setActiveModal('register')} />
                <AdminPage />
                {activeModal && (
                  <AuthModal
                    type={activeModal}
                    isOpen={true}
                    onClose={() => setActiveModal(null)}
                  />
                )}
              </ProfileLayout>
            </RequireAdmin>
          } />
          <Route path="/admin/fine-tuning-graph" element={
            <RequireAdmin>
              <ProfileLayout>
                <Header onLoginClick={() => setActiveModal('login')} onRegisterClick={() => setActiveModal('register')} />
                <FineTuningGraphPage />
                {activeModal && (
                  <AuthModal
                    type={activeModal}
                    isOpen={true}
                    onClose={() => setActiveModal(null)}
                  />
                )}
              </ProfileLayout>
            </RequireAdmin>
          } />
          <Route path="/datasets" element={
            <RequireAuth>
              <SidebarLayout>
                <Header onLoginClick={() => setActiveModal('login')} onRegisterClick={() => setActiveModal('register')} />
                <DatasetsPage />
                {activeModal && (
                  <AuthModal
                    type={activeModal}
                    isOpen={true}
                    onClose={() => setActiveModal(null)}
                  />
                )}
              </SidebarLayout>
            </RequireAuth>
          } />
        </Routes>
      </AuthWrapper>
    </AuthProvider>
  );
};

export default App;